import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { setInmobiliariaApi, fetchInmobiliariasConfigApi } from '../services/configApiClient';

export interface InmobiliariaMapping {
  ENTREGA?: string;
  EVALUACION_OFERTA?: string;
  GESTION_ESCRITURACION?: string;
  GESTION_VISITA?: string;
  PRESENTACION_OFERTA?: string;
  PROMESA_COMPRAVENTA?: string;
  SEPARACION?: string;
  VISITA_CONFIRMADA?: string;
  SUB_INMOBILIARIA?: string;
}

export const useInmobiliariaCsvProcessor = () => {
  const [file, setFile] = useState<File | null>(null);
  const [data, setData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [mapping, setMapping] = useState<InmobiliariaMapping>({
    ENTREGA: '',
    EVALUACION_OFERTA: '',
    GESTION_ESCRITURACION: '',
    GESTION_VISITA: '',
    PRESENTACION_OFERTA: '',
    PROMESA_COMPRAVENTA: '',
    SEPARACION: '',
    VISITA_CONFIRMADA: '',
    SUB_INMOBILIARIA: '',
  });

  const normalizeHeader = (str: string) => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .trim();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (event) => {
        const arrayBuffer = event.target?.result as ArrayBuffer;
        if (!arrayBuffer) return;

        const isCsv = selectedFile.name.toLowerCase().endsWith('.csv');
        let wb;

        if (isCsv) {
          const decoder = new TextDecoder('utf-8');
          const text = decoder.decode(arrayBuffer);
          wb = XLSX.read(text, { type: 'string' });
        } else {
          wb = XLSX.read(arrayBuffer, { type: 'array' });
        }

        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const json: any[] = XLSX.utils.sheet_to_json(ws);

        if (json.length > 0) {
          const h = Object.keys(json[0]).sort();
          setHeaders(h);
          setData(json);

          const newMapping = { ...mapping };
          Object.keys(newMapping).forEach((field) => {
            const normalizedField = normalizeHeader(field);
            const match = h.find((header) => normalizeHeader(header) === normalizedField);
            if (match) (newMapping as any)[field] = match;
          });
          setMapping(newMapping);
        }
      };
      reader.readAsArrayBuffer(selectedFile);
    }
  };

  const handleMappingChange = (field: keyof InmobiliariaMapping, value: string) => {
    setMapping((prev) => ({ ...prev, [field]: value }));
  };

  const processFile = async (targetDocId: string, globalSubKey: string, dataToProcess: any[]) => {
    setIsProcessing(true);
    try {
      const config = await fetchInmobiliariasConfigApi();
      const currentDoc = config[targetDocId] || {};
      const updatedDoc = { ...currentDoc };

      for (const row of dataToProcess) {
        const subKey = globalSubKey || (mapping.SUB_INMOBILIARIA ? row[mapping.SUB_INMOBILIARIA] : '') || '';
        if (!subKey) continue;

        const rowData: Record<string, any> = updatedDoc[subKey] ? { ...updatedDoc[subKey] } : {};
        (Object.entries(mapping) as [keyof InmobiliariaMapping, string][]).forEach(([field, colName]) => {
          if (field !== 'SUB_INMOBILIARIA' && colName && row[colName] !== undefined) {
            rowData[field] = row[colName];
          }
        });
        updatedDoc[subKey] = rowData;
      }

      await setInmobiliariaApi(targetDocId, updatedDoc);
      return true;
    } catch (error) {
      console.error('Error processing CSV:', error);
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    file,
    data,
    headers,
    mapping,
    isProcessing,
    handleFileChange,
    handleMappingChange,
    processFile,
  };
};
